import { FinancialAccount } from "@common/types/financialAccount";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { useEntitiesById } from "@hooks/useEntities";
import {
    useOpeningBalance,
    useRemoveOpeningBalanceMutation,
    useUpsertOpeningBalanceMutation,
} from "@api/openingBalance.api";
import { FormikProvider, useFormik } from "formik";
import { useEffect, useRef } from "react";
import { validateFormikWithZodSchema } from "@helpers/form";
import { sumMonetaryValues } from "@common/helpers/monetary";
import {
    getFinancialAccountOpeningBalanceAdjustmentDate,
    getFinancialAccountOpeningBalanceDate,
    getFinancialAccountOpeningBalanceDescription,
} from "@common/helpers/openingBalance";
import { useAccounts } from "@hooks/useAccounts";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { OpeningBalanceForm, OpeningBalanceFormSchema } from "./helpers";
import { FinancialAccountOpeningBalanceModalContent } from "./FinancialAccountOpeningBalanceModalContent";

interface Props {
    financialAccount: FinancialAccount;
    suggestedOpeningBalance?: number;
    onClose: (newOpeningBalance?: number | null) => void;
}

export const FinancialAccountOpeningBalanceModal: React.FC<Props> = ({
    financialAccount,
    suggestedOpeningBalance,
    onClose,
}) => {
    if (!financialAccount.entityId) {
        throw new Error("Financial account entity ID is required");
    }
    const entities = useEntitiesById();
    const entity = entities.get(financialAccount.entityId);
    if (!entity) {
        throw new Error("Entity not found");
    }

    const { accounts } = useAccounts({
        entityId: entity.id,
        ledgerBasis: AccountingLedgerBasis.cash,
    });
    const account = accounts.find(
        (coaAccount) => coaAccount.financialAccountId === financialAccount.id,
    );
    const upsertOpeningBalanceMutation = useUpsertOpeningBalanceMutation();
    const removeOpeningBalanceMutation = useRemoveOpeningBalanceMutation();
    const { data: openingBalanceData, isLoading: isLoadingOpeningBalance } =
        useOpeningBalance(entity.id, account?.id, AccountingLedgerBasis.cash);

    const defaultOpeningBalanceDate = getFinancialAccountOpeningBalanceDate(
        entity,
        financialAccount,
    );
    const isOpeningBalanceLocked =
        (!!openingBalanceData?.openingBalance &&
            openingBalanceData.openingBalance.date !==
                defaultOpeningBalanceDate) ||
        Boolean(openingBalanceData?.activityExcludingMainOpeningBalance);

    const formik = useFormik<OpeningBalanceForm>({
        initialValues: {
            openingBalance: suggestedOpeningBalance,
            adjustment: undefined,
        },
        validateOnMount: true,
        validate: (values) => {
            const errors = validateFormikWithZodSchema(
                OpeningBalanceFormSchema,
            )(values);

            if (
                values.openingBalance &&
                values.openingBalance !== 0 &&
                !values.clearingAccountId
            ) {
                errors.clearingAccountId = "Balancing account is required";
            }
            if (
                values.adjustment &&
                values.adjustment !== 0 &&
                !values.adjustmentClearingAccountId &&
                !openingBalanceData?.openingBalance &&
                !values.openingBalance
            ) {
                errors.adjustmentClearingAccountId =
                    "Adjustment balancing account is required";
            }

            return errors;
        },
        onSubmit: async (values) => {
            if (!account) {
                return;
            }

            const mainBalance = values.openingBalance ?? 0;
            const adjustment = values.adjustment ?? 0;
            const clearingAccountId = values.clearingAccountId;
            const hasExistingState =
                !!openingBalanceData?.openingBalance ||
                !!openingBalanceData?.adjustment;

            if (mainBalance === 0 && adjustment === 0) {
                if (hasExistingState) {
                    await removeOpeningBalanceMutation.mutateAsync({
                        entityId: entity.id,
                        accountId: account.id,
                    });
                    onClose(null);
                } else {
                    onClose(undefined);
                }
                return;
            }
            if (mainBalance !== 0 && !clearingAccountId) {
                return;
            }

            const openingBalanceDescription =
                getFinancialAccountOpeningBalanceDescription(
                    entity,
                    financialAccount,
                );
            const openingBalance = (() => {
                if (isOpeningBalanceLocked) {
                    return undefined;
                }
                if (mainBalance !== 0 && clearingAccountId) {
                    return {
                        date: defaultOpeningBalanceDate,
                        clearingAccountId,
                        clearingBalanceDescription: openingBalanceDescription,
                        lines: [
                            {
                                balance: mainBalance,
                                description: openingBalanceDescription,
                            },
                        ],
                    };
                }
                if (openingBalanceData?.openingBalance) {
                    return null;
                }
                return undefined;
            })();

            const response = await upsertOpeningBalanceMutation.mutateAsync({
                params: {
                    entityId: entity.id,
                    accountId: account.id,
                },
                query: { ledgerBasis: AccountingLedgerBasis.cash },
                body: {
                    openingBalance,
                    adjustment:
                        adjustment === 0
                            ? null
                            : {
                                  balance: adjustment,
                                  date: getFinancialAccountOpeningBalanceAdjustmentDate(
                                      entity,
                                      financialAccount,
                                  ),
                                  clearingAccountId:
                                      values.adjustmentClearingAccountId,
                                  description: "Opening balance adjustment",
                              },
                },
            });

            onClose(
                sumMonetaryValues([
                    response.activityExcludingMainOpeningBalance,
                    ...(response.openingBalance?.lines.map(
                        (line) => line.balance,
                    ) ?? []),
                    response.adjustment?.balance ?? 0,
                ]),
            );
        },
    });

    const { setValues } = formik;
    const initialFetchDone = useRef(false);

    useEffect(() => {
        if (initialFetchDone.current || !openingBalanceData) {
            return;
        }
        initialFetchDone.current = true;

        setValues((currentValues) => {
            let openingBalanceToUse = currentValues.openingBalance;
            if (openingBalanceData.openingBalance) {
                openingBalanceToUse = sumMonetaryValues(
                    openingBalanceData.openingBalance.lines.map(
                        (line) => line.balance,
                    ),
                );
            } else if (openingBalanceData.activityExcludingMainOpeningBalance) {
                openingBalanceToUse = undefined;
            }

            return {
                openingBalance: openingBalanceToUse,
                clearingAccountId:
                    openingBalanceData.openingBalance?.clearingAccountId,
                adjustment: openingBalanceData.adjustment?.balance ?? undefined,
                adjustmentClearingAccountId:
                    openingBalanceData.adjustment?.clearingAccountId,
            };
        });
    }, [openingBalanceData, setValues]);

    return (
        <>
            <StandardModalHeader>Set Opening Balance</StandardModalHeader>
            <StandardModalBody>
                <FormikProvider value={formik}>
                    <FinancialAccountOpeningBalanceModalContent
                        financialAccount={financialAccount}
                        entity={entity}
                        onClose={onClose}
                        isLoading={isLoadingOpeningBalance}
                        existingOpeningBalance={
                            openingBalanceData?.openingBalance
                        }
                        activityExcludingMainOpeningBalance={
                            openingBalanceData?.activityExcludingMainOpeningBalance ??
                            0
                        }
                        isSaving={
                            upsertOpeningBalanceMutation.isPending ||
                            removeOpeningBalanceMutation.isPending
                        }
                        isOpeningBalanceLocked={isOpeningBalanceLocked}
                    />
                </FormikProvider>
            </StandardModalBody>
        </>
    );
};
